// src/App.js
import React, { useEffect, useState } from "react";
import api from "./api/axios";
import MobileSchedulePage from "./pages/MobileSchedulePage";
import DriversPage from "./pages/DriversPage";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import deDE from "antd/locale/de_DE";

// Seiten (Public)
import Home from "./pages/Home";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Impressum from "./pages/Impressum";
import ContactPage from "./pages/ContactPage";
import Datenschutz from "./pages/Datenschutz";
import UeberMich from "./pages/UeberMich";

// Seiten (App-intern)
import Dashboard from "./pages/Dashboard";
import PassengersPage from "./pages/PassengersPage";
import BusesPage from "./pages/BusesPage";
import CostsPage from "./pages/CostsPage";
import RoutesPage from "./pages/RoutesPage";
import ResultsPage from "./pages/ResultsPage";
import MapsPage from "./pages/MapsPage";
import AccountPage from "./pages/AccountPage";
import AdminPage from "./pages/AdminPage";
import TariffsPage from "./pages/TarifePage";
import PlanningSettingsPage from "./pages/PlanningSettingsPage";

// Layout
import AppLayout from "./components/AppLayout";

function RequireAuth({ children, driver = false }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let active = true;
    api.get("/users/me").then(({ data }) => {
      if (!active) return;
      localStorage.setItem("user", JSON.stringify(data));
      setState({ user: data });
    }).catch(() => { if (active) setState({ error: true }); });
    return () => { active = false; };
  }, []);
  if (!localStorage.getItem("token") || state.error) return <Navigate to="/login" replace />;
  if (state.loading) return <div role="status" style={{ padding: 24 }}>Zugang wird geladen …</div>;
  const isDriver = state.user.roles?.some(role => ["DRIVER", "ROLE_DRIVER"].includes(role));
  if (isDriver !== driver) return <Navigate to={isDriver ? "/driver" : "/app/mobile"} replace />;
  return children;
}

export default function App() {
  return (
    <ConfigProvider
      locale={deDE}
      theme={{
        token: {
          colorPrimary: "#3FB6C2",
          colorLink: "#3FB6C2",
          colorInfo: "#3FB6C2",

          colorSuccess: "#52c41a",
          colorWarning: "#faad14",
          colorError: "#ff4d4f",

          colorBgLayout: "#F4FBFC",
          colorBgContainer: "#FFFFFF",
          colorText: "#1F2937",
          colorTextSecondary: "#6B7280",
          colorBorder: "#DCE7F3",

          borderRadius: 10,
        },
        components: {
          Button: {
            colorPrimary: "#3FB6C2",
            colorPrimaryHover: "#36A3AE",
            colorPrimaryActive: "#2C8C96",
          },
          Layout: {
            headerBg: "#FFFFFF",
            siderBg: "#1F4E57",
            bodyBg: "#F4FBFC",
          },
          Menu: {
            darkItemBg: "#1F4E57",
            darkSubMenuItemBg: "#1F4E57",
            darkItemSelectedBg: "#3FB6C2",
            darkItemHoverBg: "#2E6F78",
            darkItemColor: "#D8F1F4",
            darkItemSelectedColor: "#FFFFFF",
          },
          Table: {
            headerBg: "#F4FBFC",
            rowHoverBg: "#E8F7F9",
          },
        },
      }}
    >
      <Router>
        <Routes>
          {/* Öffentliche Seiten */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/ueber-mich" element={<UeberMich />} />

          <Route path="/driver" element={<RequireAuth driver><MobileSchedulePage driver /></RequireAuth>} />
          {/* Geschützte Seiten */}
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="mobile" element={<MobileSchedulePage />} />
            <Route path="drivers" element={<DriversPage />} />
            <Route path="tariffs" element={<TariffsPage />} />
            <Route path="passengers" element={<PassengersPage />} />
            <Route path="buses" element={<BusesPage />} />
            <Route path="costfactors" element={<CostsPage />} />
            <Route path="planning-settings" element={<PlanningSettingsPage />} />
            <Route path="routes" element={<RoutesPage />} />
            <Route path="results" element={<ResultsPage />} />
            <Route path="maps" element={<MapsPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="admin" element={<AdminPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}
